import { FC } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/helpers/cn";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import {
  BrushIcon,
  Document6Icon,
  GridIcon,
  House2Icon,
  MailIcon,
  Refresh2Icon,
  RubleIcon,
  SofaIcon,
  UserIcon,
  Vehicle2Icon,
} from "@/shared/icons";
import { BaseIconProps } from "@/shared/icons/BaseIcon";
import { Button } from "@/shared/ui";
import { Tab } from "@/domains/estate/constants/tab";
import { currentTabSelector, setCurrentTab, tabsSelector } from "@/domains/estate/slice";

type Props = {
  className?: string;
  changeEstate: () => void;
};
const iconTab: Record<Tab, FC<BaseIconProps>> = {
  [Tab.Info]: House2Icon,
  [Tab.Tenants]: UserIcon,
  [Tab.Fee]: RubleIcon,
  [Tab.Garage]: Vehicle2Icon,
  [Tab.Walls]: BrushIcon,
  [Tab.Furniture]: SofaIcon,
  [Tab.Mail]: MailIcon,
  [Tab.Improvements]: GridIcon,
  [Tab.History]: Document6Icon,
};

export default function EstateTabsContainer({ className, changeEstate }: Props) {
  const tabs = useAppSelector(tabsSelector);
  const currentTab = useAppSelector(currentTabSelector);
  const { t } = useTranslation("estate");
  const dispatch = useAppDispatch();

  return (
    <div className={cn("flex flex-col gap-1.5 w-56 shrink-0", className)}>
      <div className="flex items-center gap-2.5 mb-3">
        <img
          src="/svg/home-menu/my-estate.svg"
          className="w-[2.625rem] h-[2.625rem]"
          alt="estate"
        />
        <div>
          <div className="text-15 font-bold text-inverted uppercase">
            {t("estate.header.title")}
          </div>
          <div className="text-14 font-medium text-gray/50">{t("estate.header.description")}</div>
        </div>
      </div>
      {tabs.map((tab) => (
        <Button
          key={tab}
          variant={currentTab === tab ? "solid-gradient" : "soft"}
          color={currentTab === tab ? "accent" : "gray"}
          className="h-[2.75rem] px-3.5 flex justify-start items-center gap-2.5"
          onClick={() => dispatch(setCurrentTab(tab))}
        >
          {iconTab[tab]({ size: 16 })}
          {t(`estate.tabs.${tab}`)}
        </Button>
      ))}
      <Button
        variant="soft"
        className="flex justify-start items-center gap-2.5 h-14.5 mt-auto px-3.5 bg-dark-800/50"
        onClick={changeEstate}
      >
        <Refresh2Icon size={16} />
        <div className="flex flex-col items-start">
          <p>{t("estate.tabs.changeHome")}</p>
          <p className="text-14 text-white/30 font-medium normal-case">
            {t("estate.tabs.selectHome")}
          </p>
        </div>
      </Button>
    </div>
  );
}
